import type { RepositorySettings } from '@/decorators/repositoryDecorators'
import type { Endpoint } from '@/services/endpoints'
import SipCall from '@/data/models/domain/SipCall'
import type Invoice from '@/data/models/domain/billing/Invoice'
import type EmailCampaignMessage from '@/data/models/domain/campaign/EmailCampaignMessage'
import type { ModelRaw, Nullable } from '@/types'
import type EmailCampaignOpen from '@/data/models/domain/campaign/EmailCampaignOpen'
import type { BonusEmailStatus, FollowUpSendTo, FollowUpTimeUnit, IFollowUpRelated } from '@/data/models/domain/types'
import type { RecipientType } from '@/services/domain/messages/types'
import type MessagesPreviewRecipient from '@/data/models/domain/MessagesPreviewRecipient'
import type Label from '@/data/models/domain/Label'

export const sipCallsRepoSettings = {
  model: SipCall,
  single: 'sipCallSingle',
  bulkDeleteEndpoint: 'sipCallsBulkDelete',
  exportEndpoint: 'sipCallExport',
  pdfDownloadEndpoint: 'sipCallPdf',
} as RepositorySettings<Endpoint>

export type InvoiceByProviderSessionLoadingResult =
  | {
      invoice: Invoice
      isDeferred: false
    }
  | {
      invoice: null
      isDeferred: true
    }

interface OutboundEmailDetailsResponseEngagement {
  spam: null | Record<string, never>
  unsubscribe: null | Record<string, never>
  open: null | ModelRaw<EmailCampaignOpen>
  firstClick: null | Record<string, never>
}

interface OutboundEmailDetailsFollowUpDetails extends IFollowUpRelated {
  id: string
  sendAfter: number
  sendAfterUnit: FollowUpTimeUnit
  sendTo: FollowUpSendTo
}

interface OutboundEmailDetailsFollowUp extends IFollowUpRelated {
  id: string
  sendAfter: number
  sendAfterUnit: FollowUpTimeUnit
  sendTo: FollowUpSendTo
  setDifferentSubject: boolean
  emailSubject: string | null
  previewText: string | null
}

export enum OutboundEmailSource {
  webApp = 'webApp',
  api = 'api',
  iosApp = 'iosApp',
  androidApp = 'androidApp',
  automation = 'automation',
}

export interface OutboundEmailDetailsResponse {
  isPartOfBulkSending: boolean
  engagement: OutboundEmailDetailsResponseEngagement
  outboundEmailId: string
  outboundEmail: ModelRaw<EmailCampaignMessage>
  followUpDetails: OutboundEmailDetailsFollowUpDetails | null
  followUp: OutboundEmailDetailsFollowUp | null
  isSentUsingBonus: boolean
  createdByAutomationId: string | null
  labels: Label[]
  source: Nullable<OutboundEmailSource>
}

export interface BonusInfoResponse {
  email: {
    emails: number
    lookups: number
    status: BonusEmailStatus
  }
}

export interface IRecipientsAutocompleteRequestBody {
  query: string
  withLists: boolean
  withFilteredViews: boolean
  searchBy: 'phone' | 'email' | 'both'
  limit?: number
}

export type RecipientsAutocompleteItemEntityType = Extract<
  RecipientType,
  'contact' | 'list' | 'reply' | 'filtered_view' | 'contact_email' | 'reply_email'
>

export interface IRecipientsAutocompleteItem {
  entityType: RecipientsAutocompleteItemEntityType
  entityId: number
  title: string
  /**
   * Phone for contact, any string for another entity types
   * @see {@link https://textmagic.atlassian.net/browse/CAR-15100?focusedCommentId=107876}
   */
  value1?: string
  /**
   * Email
   * @see {@link https://textmagic.atlassian.net/browse/CAR-15100?focusedCommentId=107876}
   */
  value2?: string
  countryName: string
  avatarHref?: string
  sharedBy?: string
}

export interface IContactGridRequestRequestBody {
  perPage?: number
  page?: number
  sort?: unknown
  searchQuery?: string
  filters?: unknown
  shared?: boolean
}

export interface PreviewMessagesResponse {
  charset: string
  cost: number
  messages: MessagesPreviewRecipient[]
  recipientsCount: number
}
